import { useState, useEffect } from 'react';
import apiClient from '../api/client';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { TrendingUp, DollarSign, Target, Award, Edit, Link2, Settings, Eye, Trophy, Users } from 'lucide-react';
import GoalOpportunityManagerModal from '../components/goals/GoalOpportunityManagerModal';
import GoalAssignmentRulesPanel from '../components/goals/GoalAssignmentRulesPanel';
import GoalsLeaderboard from '../components/goals/GoalsLeaderboard';
import TeamDashboard from '../components/goals/TeamDashboard';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const Goals = () => {
  const [goals, setGoals] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [activeTab, setActiveTab] = useState('goals'); // 'goals' or 'analytics'
  
  // Analytics state
  const [analytics, setAnalytics] = useState({
    overview: null,
    forecast: null,
    conversions: [],
    trends: []
  });
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    period: 'monthly',
    start_date: '',
    end_date: '',
    target_revenue_cents: 0,
    target_opportunity_count: 0,
    assignment_type: 'user',
    user_id: '',
    team_id: ''
  });
  
  // Opportunity Manager Modal state
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [selectedGoalForOpportunities, setSelectedGoalForOpportunities] = useState(null);
  
  // Assignment Rules Panel state
  const [showRulesPanel, setShowRulesPanel] = useState(false);
  const [selectedGoalForRules, setSelectedGoalForRules] = useState(null);
  
  // View As state (for admin/manager)
  const [viewAsOptions, setViewAsOptions] = useState(null);
  const [viewingAsUser, setViewingAsUser] = useState(null);
  
  // Team Dashboard state
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [showTeamDashboard, setShowTeamDashboard] = useState(false);

  useEffect(() => {
    loadData();
    loadViewAsOptions();
  }, [filterPeriod]);

  const loadViewAsOptions = async () => {
    try {
      const response = await apiClient.get('/api/v1/auth/view-as-options');
      setViewAsOptions(response.data.data);
    } catch (error) {
      console.error('Error loading view-as options:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load goals
      const params = filterPeriod !== 'all' ? { period: filterPeriod } : {};
      const goalsResponse = await apiClient.get('/api/v1/goals', { params });
      setGoals(goalsResponse.data.data || []);
      
      // Load teams
      const teamsResponse = await apiClient.get('/api/v1/goals/teams');
      setTeams(teamsResponse.data.data || []);
      
      // Load analytics
      const [overviewRes, forecastRes, conversionsRes, trendsRes] = await Promise.all([
        apiClient.get('/api/v1/analytics/overview', { params }),
        apiClient.get('/api/v1/analytics/forecast'),
        apiClient.get('/api/v1/analytics/conversion-rates', { params }),
        apiClient.get('/api/v1/analytics/revenue-trends', { params: { period: 'monthly', lookback_periods: 6 } })
      ]);
      
      setAnalytics({
        overview: overviewRes.data.data,
        forecast: forecastRes.data.data,
        conversions: conversionsRes.data.data.conversions || [],
        trends: trendsRes.data.data.trends || []
      });
      
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        period: formData.period,
        start_date: formData.start_date,
        end_date: formData.end_date,
        target_revenue_cents: Math.round(parseFloat(formData.target_revenue_cents) * 100),
        target_opportunity_count: parseInt(formData.target_opportunity_count),
      };
      
      if (formData.assignment_type === 'user') {
        payload.user_id = formData.user_id || localStorage.getItem('user_id');
      } else {
        payload.team_id = formData.team_id;
      }
      
      await apiClient.post('/api/v1/goals', null, { params: payload });
      
      setShowCreateDialog(false);
      resetForm();
      loadData();
      alert('Goal created successfully!');
    } catch (error) {
      console.error('Error creating goal:', error);
      alert('Failed to create goal: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditGoal = (goal) => {
    setSelectedGoal(goal);
    setFormData({
      name: goal.name,
      description: goal.description || '',
      period: goal.period,
      start_date: goal.start_date,
      end_date: goal.end_date,
      target_revenue_cents: goal.target_revenue_cents / 100,
      target_opportunity_count: goal.target_opportunity_count,
      assignment_type: goal.owner_type,
      user_id: goal.owner_type === 'user' ? goal.user_id : '',
      team_id: goal.owner_type === 'team' ? goal.team_id : ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateGoal = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        target_revenue_cents: Math.round(parseFloat(formData.target_revenue_cents) * 100),
        target_opportunity_count: parseInt(formData.target_opportunity_count),
      };
      
      await apiClient.put(`/api/v1/goals/${selectedGoal.id}`, null, { params: payload });
      
      setShowEditDialog(false);
      setSelectedGoal(null);
      resetForm();
      loadData();
      alert('Goal updated successfully!');
    } catch (error) {
      console.error('Error updating goal:', error);
      alert('Failed to update goal: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    
    try {
      await apiClient.delete(`/api/v1/goals/${goalId}`);
      loadData();
      alert('Goal deleted successfully!');
    } catch (error) {
      console.error('Error deleting goal:', error);
      alert('Failed to delete goal');
    }
  };

  const handleManageOpportunities = (goal) => {
    setSelectedGoalForOpportunities(goal);
    setShowOpportunityModal(true);
  };

  const handleOpportunityModalClose = () => {
    setShowOpportunityModal(false);
    setSelectedGoalForOpportunities(null);
    // Refresh data to get updated progress
    loadData();
  };

  const handleManageRules = (goal) => {
    setSelectedGoalForRules(goal);
    setShowRulesPanel(true);
  };

  const handleRulesPanelClose = () => {
    setShowRulesPanel(false);
    setSelectedGoalForRules(null);
    loadData();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      period: 'monthly',
      start_date: '',
      end_date: '',
      target_revenue_cents: 0,
      target_opportunity_count: 0,
      assignment_type: 'user',
      user_id: '',
      team_id: ''
    });
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 75) return 'bg-blue-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      missed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || colors.active}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-gray-600">Loading goals...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Goals & Analytics</h1>
          <p className="text-gray-600 mt-1">Track goals and analyze pipeline performance</p>
        </div>
        <div className="flex items-center gap-4">
          {/* View As Dropdown (admin/manager only) */}
          {viewAsOptions?.can_view_as && (
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-gray-500" />
              <select
                value={viewingAsUser || ''}
                onChange={(e) => setViewingAsUser(e.target.value || null)}
                className="px-3 py-2 border rounded-lg text-sm bg-yellow-50 border-yellow-300"
              >
                <option value="">
                  Viewing as: {viewAsOptions.current_user?.name} ({viewAsOptions.current_user?.role})
                </option>
                {viewAsOptions.users?.map((user) => (
                  <option key={user.id} value={user.id}>
                    View as: {user.name} ({user.role})
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={() => setShowCreateDialog(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            + Create Goal
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-300 mb-6">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('goals')}
            className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'goals'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Target className="w-4 h-4" />
            Goals
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'leaderboard'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Trophy className="w-4 h-4" />
            Leaderboard
          </button>
          <button
            onClick={() => setActiveTab('teams')}
            className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'teams'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="w-4 h-4" />
            Teams
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'analytics'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Analytics
          </button>
        </div>
      </div>

      {/* Goals Tab Content */}
      {activeTab === 'goals' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex gap-4 items-center">
              <label className="text-sm font-medium text-gray-700">Filter by Period:</label>
              <select
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm"
              >
                <option value="all">All Periods</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
              </select>
            </div>
          </div>

          {/* Goals Grid */}
          {goals.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <p className="text-gray-500 text-lg">No goals found. Create your first goal to get started!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {goals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onEdit={handleEditGoal}
                  onDelete={handleDeleteGoal}
                  onManageOpportunities={handleManageOpportunities}
                  onManageRules={handleManageRules}
                  formatCurrency={formatCurrency}
                  getProgressColor={getProgressColor}
                  getStatusBadge={getStatusBadge}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Leaderboard Tab Content */}
      {activeTab === 'leaderboard' && (
        <div className="max-w-4xl mx-auto">
          <GoalsLeaderboard teams={teams} />
        </div>
      )}

      {/* Teams Tab Content */}
      {activeTab === 'teams' && (
        <div>
          {showTeamDashboard && selectedTeamId ? (
            <TeamDashboard 
              teamId={selectedTeamId} 
              onClose={() => {
                setShowTeamDashboard(false);
                setSelectedTeamId(null);
              }}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {teams.length === 0 ? (
                <div className="col-span-full text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No teams found</p>
                  <p className="text-sm">Create a team to see aggregated performance</p>
                </div>
              ) : (
                teams.map((team) => (
                  <div
                    key={team.id}
                    className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition cursor-pointer"
                    onClick={() => {
                      setSelectedTeamId(team.id);
                      setShowTeamDashboard(true);
                    }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{team.name}</h3>
                        {team.description && (
                          <p className="text-xs text-gray-500 truncate">{team.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      Click to view team dashboard
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab Content */}
      {activeTab === 'analytics' && analytics.overview && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <KPICard
              title="Total Pipeline"
              value={formatCurrency(analytics.overview.total_pipeline_value_cents)}
              subtitle={`${analytics.overview.total_opportunities} opportunities`}
              icon={<DollarSign className="w-6 h-6" />}
              color="blue"
            />
            <KPICard
              title="Win Rate"
              value={`${analytics.overview.win_rate_percent}%`}
              subtitle={`${analytics.overview.awarded_count} won, ${analytics.overview.lost_count} lost`}
              icon={<Target className="w-6 h-6" />}
              color="green"
            />
            <KPICard
              title="Avg Deal Size"
              value={formatCurrency(analytics.overview.average_deal_size_cents)}
              subtitle="Per won opportunity"
              icon={<Award className="w-6 h-6" />}
              color="yellow"
            />
            <KPICard
              title="Forecast"
              value={formatCurrency(analytics.forecast?.total_weighted_forecast_cents || 0)}
              subtitle="Weighted pipeline value"
              icon={<TrendingUp className="w-6 h-6" />}
              color="purple"
            />
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pipeline Funnel */}
            <ChartCard title="Pipeline Funnel">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={[
                  { stage: 'Target', value: analytics.overview.stage_breakdown.target?.total_value_cents || 0 },
                  { stage: 'Qualified', value: analytics.overview.stage_breakdown.qualified?.total_value_cents || 0 },
                  { stage: 'RFQ', value: analytics.overview.stage_breakdown.rfq?.total_value_cents || 0 },
                  { stage: 'Awarded', value: analytics.overview.stage_breakdown.awarded?.total_value_cents || 0 },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="value" fill="#3b82f6" name="Total Value" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Revenue Trends */}
            <ChartCard title="Revenue Trends (Last 6 Months)">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analytics.trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Line type="monotone" dataKey="revenue_cents" stroke="#10b981" strokeWidth={2} name="Revenue" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Conversion Rates */}
            <ChartCard title="Stage Conversion Rates">
              <div className="space-y-4 p-4">
                {analytics.conversions.map((conv, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{conv.label}</span>
                      <span className="text-gray-600">{conv.conversion_rate_percent}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${conv.conversion_rate_percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </ChartCard>

            {/* Forecast by Stage */}
            {analytics.forecast && (
              <ChartCard title="Weighted Forecast by Stage">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analytics.forecast.by_stage}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ stage, weighted_value_cents }) => 
                        `${stage}: ${formatCurrency(weighted_value_cents)}`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="weighted_value_cents"
                    >
                      {analytics.forecast.by_stage.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        </>
      )}

      {/* Edit Goal Dialog */}
      {showEditDialog && selectedGoal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Edit Goal</h2>
            
            <form onSubmit={handleUpdateGoal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Goal Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Revenue ($) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.target_revenue_cents}
                    onChange={(e) => setFormData({ ...formData, target_revenue_cents: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Opportunity Count *
                  </label>
                  <input
                    type="number"
                    value={formData.target_opportunity_count}
                    onChange={(e) => setFormData({ ...formData, target_opportunity_count: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditDialog(false);
                    setSelectedGoal(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Update Goal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Goal Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Create New Goal</h2>
            
            <form onSubmit={handleCreateGoal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Goal Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period *</label>
                  <select
                    value={formData.period}
                    onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign To *</label>
                  <select
                    value={formData.assignment_type}
                    onChange={(e) => setFormData({ ...formData, assignment_type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="user">Individual (Me)</option>
                    <option value="team">Team</option>
                  </select>
                </div>
              </div>

              {formData.assignment_type === 'team' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Team *</label>
                  <select
                    value={formData.team_id}
                    onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required={formData.assignment_type === 'team'}
                  >
                    <option value="">-- Select Team --</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Revenue ($) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.target_revenue_cents}
                    onChange={(e) => setFormData({ ...formData, target_revenue_cents: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Opportunity Count *
                  </label>
                  <input
                    type="number"
                    value={formData.target_opportunity_count}
                    onChange={(e) => setFormData({ ...formData, target_opportunity_count: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateDialog(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create Goal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Goal Opportunity Manager Modal */}
      <GoalOpportunityManagerModal
        goal={selectedGoalForOpportunities}
        isOpen={showOpportunityModal}
        onClose={handleOpportunityModalClose}
        onUpdate={loadData}
      />

      {/* Goal Assignment Rules Panel */}
      {showRulesPanel && selectedGoalForRules && (
        <GoalAssignmentRulesPanel
          goal={selectedGoalForRules}
          onClose={handleRulesPanelClose}
          onRulesChanged={loadData}
        />
      )}
    </div>
  );
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

// Chart Card Component
const ChartCard = ({ title, children }) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      {children}
    </div>
  );
};

// Goal Card Component
const GoalCard = ({ goal, onEdit, onDelete, onManageOpportunities, onManageRules, formatCurrency, getProgressColor, getStatusBadge }) => {
  const [progressSummary, setProgressSummary] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [autoMatchedData, setAutoMatchedData] = useState(null);
  const [pacingData, setPacingData] = useState(null);

  const loadProgressSummary = async () => {
    try {
      setLoadingProgress(true);
      // Load all data in parallel
      const [autoMatchedRes, progressRes, pacingRes] = await Promise.all([
        apiClient.get(`/api/v1/goals/${goal.id}/auto-matched`),
        apiClient.get(`/api/v1/goals/${goal.id}/progress-summary`),
        apiClient.get(`/api/v1/goals/${goal.id}/pacing`)
      ]);
      
      setAutoMatchedData(autoMatchedRes.data.data);
      setProgressSummary(progressRes.data.data);
      setPacingData(pacingRes.data.data);
    } catch (error) {
      console.error('Error loading progress summary:', error);
    } finally {
      setLoadingProgress(false);
    }
  };

  useEffect(() => {
    if (goal.status === 'active') {
      loadProgressSummary();
    }
  }, [goal.id]);

  const formatShortCurrency = (cents) => {
    if (!cents) return '$0';
    const value = cents / 100;
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  const getPacingStatusBadge = (status) => {
    const configs = {
      achieved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Achieved' },
      ahead: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Ahead' },
      on_track: { bg: 'bg-green-100', text: 'text-green-700', label: 'On Track' },
      behind: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Behind' },
      at_risk: { bg: 'bg-red-100', text: 'text-red-800', label: 'At Risk' },
      completed: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Completed' },
      missed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Missed' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Cancelled' }
    };
    const config = configs[status] || configs.on_track;
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div 
      className="bg-white rounded-lg shadow hover:shadow-lg transition p-6 cursor-pointer"
      onClick={() => onManageOpportunities(goal)}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{goal.name}</h3>
          <p className="text-sm text-gray-600">
            {goal.owner_name} | {goal.period}
          </p>
        </div>
        {getStatusBadge(goal.status)}
      </div>

      {/* Description */}
      {goal.description && (
        <p className="text-sm text-gray-600 mb-4">{goal.description}</p>
      )}

      {/* Date Range */}
      <div className="text-sm text-gray-500 mb-4">
        {new Date(goal.start_date).toLocaleDateString()} - {new Date(goal.end_date).toLocaleDateString()}
      </div>

      {/* Pacing Dashboard - NEW */}
      {pacingData && (
        <div className="mb-4 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-indigo-700">Pacing</span>
              {getPacingStatusBadge(pacingData.pacing.status)}
            </div>
            <span className="text-xs text-gray-500">
              {pacingData.time.days_remaining} days left
            </span>
          </div>
          
          {/* Time Progress Bar */}
          <div className="relative mb-2">
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-indigo-400 h-1.5 rounded-full"
                style={{ width: `${Math.min(pacingData.time.time_progress_percent, 100)}%` }}
              />
            </div>
            {/* Progress marker */}
            <div 
              className="absolute top-0 w-0.5 h-3 bg-indigo-600 -mt-0.5"
              style={{ left: `${Math.min(pacingData.progress.revenue_progress_percent, 100)}%` }}
              title={`Actual: ${pacingData.progress.revenue_progress_percent}%`}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-gray-500">Current pace:</span>
              <span className="ml-1 font-medium">{formatShortCurrency(pacingData.pacing.current_daily_rate_cents)}/day</span>
            </div>
            <div>
              <span className="text-gray-500">Need:</span>
              <span className="ml-1 font-medium text-indigo-600">{formatShortCurrency(pacingData.pacing.required_daily_rate_cents)}/day</span>
            </div>
          </div>
          
          {pacingData.pacing.status !== 'achieved' && pacingData.pacing.projected_percent > 0 && (
            <div className="mt-2 text-xs text-gray-600">
              At current pace: <span className="font-medium">{formatShortCurrency(pacingData.pacing.projected_revenue_cents)}</span> 
              <span className="text-gray-400 ml-1">({pacingData.pacing.projected_percent}% of target)</span>
            </div>
          )}
        </div>
      )}

      {/* Progress Summary from Linked Opportunities */}
      {loadingProgress ? (
        <div className="text-sm text-gray-500">Loading progress...</div>
      ) : (progressSummary || autoMatchedData) ? (
        <div className="space-y-4">
          {/* Auto-Matched Summary (if rules exist) */}
          {autoMatchedData && autoMatchedData.summary?.matched_count > 0 && (
            <div className="bg-green-50 rounded-lg p-3 border border-green-100">
              <div className="text-xs text-green-600 font-medium mb-1 flex items-center gap-1">
                <Settings className="w-3 h-3" />
                Auto-Matched (via Rules)
              </div>
              <div className="text-sm text-gray-800">
                <span className="font-semibold">{autoMatchedData.summary.matched_count}</span> matched | {' '}
                <span className="text-green-600 font-semibold">{formatShortCurrency(autoMatchedData.summary.total_weighted_revenue_cents)}</span> weighted
              </div>
            </div>
          )}
          
          {/* Manually Linked Opportunities Summary */}
          {progressSummary && (
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-xs text-blue-600 font-medium mb-1 flex items-center gap-1">
                <Link2 className="w-3 h-3" />
                Manually Linked
              </div>
              <div className="text-sm text-gray-800">
                <span className="font-semibold">{progressSummary.linked_opportunity_count}</span> tracked | {' '}
                <span className="text-green-600 font-semibold">{formatShortCurrency(progressSummary.total_weighted_revenue_cents)}</span> weighted / {' '}
                <span className="font-medium">{formatShortCurrency(progressSummary.target_revenue_cents)}</span> target
              </div>
            </div>
          )}

          {/* Combined Revenue Progress Bar */}
          {progressSummary && (
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Revenue Progress</span>
                <span className="font-medium">
                  {progressSummary.revenue_progress_percent}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getProgressColor(progressSummary.revenue_progress_percent)}`}
                  style={{ width: `${Math.min(progressSummary.revenue_progress_percent, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Opportunity Count Progress */}
          {goal.target_opportunity_count > 0 && progressSummary && (
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Opportunity Count</span>
                <span className="font-medium">
                  {progressSummary.linked_opportunity_count} / {progressSummary.target_opportunity_count}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getProgressColor(progressSummary.count_progress_percent)}`}
                  style={{ width: `${Math.min(progressSummary.count_progress_percent, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-sm">
            <span className="text-gray-600">Revenue Target: </span>
            <span className="font-medium">{formatCurrency(goal.target_revenue_cents)}</span>
          </div>
          <div className="text-sm">
            <span className="text-gray-600">Opportunity Target: </span>
            <span className="font-medium">{goal.target_opportunity_count}</span>
          </div>
          <div className="text-xs text-blue-600 mt-2">Click to link opportunities or set up rules</div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between" onClick={(e) => e.stopPropagation()}>
        <div className="flex gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onManageOpportunities(goal);
            }}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
          >
            <Link2 className="w-4 h-4" />
            Link
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onManageRules(goal);
            }}
            className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center gap-1"
          >
            <Settings className="w-4 h-4" />
            Rules
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(goal);
            }}
            className="text-gray-600 hover:text-gray-700 text-sm font-medium flex items-center gap-1"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(goal.id);
          }}
          className="text-red-600 hover:text-red-700 text-sm font-medium"
        >
          Delete
        </button>
      </div>
    </div>
  );
};

export default Goals;
